import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

import { OVERLAY_LAYER } from './types/overlay-environment-variants-enums';
import { OverlayLayerBoundary } from './overlay-environment';
import { useFloatingPortalContainer, useOverlayLayerZIndex } from './overlay-environment-context';

import { cn } from '@/lib/utils';

import { POPOVER_APPEARANCE, POPOVER_SIZE } from './types/popover-variants-enums';

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;





const popoverContentClasses =
  'z-popover w-72 rounded-lg border bg-popover px-[10px] py-[6px] font-normal text-popover-foreground shadow-card outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2';
const foundationPopoverContentClasses =
  'z-popover max-w-[calc(100vw-32px)] rounded-surface border border-border-default bg-surface-raised p-3 font-normal text-content-primary shadow-raised outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2';

const popoverSizeClasses: Record<POPOVER_SIZE, string> = {
  [POPOVER_SIZE.CONTENT]: 'w-max',
  [POPOVER_SIZE.SM]: 'w-60',
  [POPOVER_SIZE.MD]: 'w-72',
  [POPOVER_SIZE.LG]: 'w-[360px]',
};

interface PopoverContentProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {
  appearance?: POPOVER_APPEARANCE;
  container?: React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Portal>['container'];
  size?: POPOVER_SIZE;
}

function getPopoverContentClasses(
  appearance: POPOVER_APPEARANCE | undefined,
  size: POPOVER_SIZE | undefined
) {
  const isFoundation = appearance !== undefined || size !== undefined;
  if (!isFoundation) return popoverContentClasses;

  return cn(
    foundationPopoverContentClasses,
    popoverSizeClasses[size ?? POPOVER_SIZE.CONTENT]
  );
}

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(
  (
    {
      appearance,
      className,
      children,
      align = 'center',
      collisionPadding,
      container,
      sideOffset,
      size,
      style,
      ...props
    },
    ref
  ) => {
    const isFoundation = appearance !== undefined || size !== undefined;
    const portalContainer = useFloatingPortalContainer(container);
    const zIndex = useOverlayLayerZIndex(OVERLAY_LAYER.POPOVER);

    return (
      <PopoverPrimitive.Portal container={portalContainer as HTMLElement | undefined}>
        <PopoverPrimitive.Content
          ref={ref}
          align={align}
          className={cn(getPopoverContentClasses(appearance, size), className)}
          collisionPadding={collisionPadding ?? (isFoundation ? 16 : undefined)}
          sideOffset={sideOffset ?? (isFoundation ? 8 : 4)}
          style={{ ...style, zIndex }}
          {...props}
        >
          <OverlayLayerBoundary zIndex={zIndex}>{children}</OverlayLayerBoundary>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    );
  }
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger };
